import {
  Box,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  DcRelevanceChange,
  DcRelevanceImportResult,
} from "../../helpers/excel-importer";

type DcRelevancePreviewModalProps = {
  open: boolean;
  onClose: () => void;
  preview: DcRelevanceImportResult;
  language: string;
};

const headerCellSx = { fontWeight: 500, backgroundColor: grey[100] };

const MAX_COLUMN_WIDTH = 60;

const valueCellSx = (value: DcRelevanceChange["from"]) => ({
  fontWeight: 500,
  color:
    value === "Yes"
      ? "success.main"
      : value === "No"
        ? "error.main"
        : grey[600],
});

type PreviewTab = "changes" | "byOe" | "unchanged" | "notFound";

type OeSummary = {
  projectName: string;
  notSetToYes: number;
  notSetToNo: number;
  noToYes: number;
  yesToNo: number;
  total: number;
  unchanged: number;
  checked: number;
  notInPimo: number;
};

const emptySummary = (projectName: string): OeSummary => ({
  projectName,
  notSetToYes: 0,
  notSetToNo: 0,
  noToYes: 0,
  yesToNo: 0,
  total: 0,
  unchanged: 0,
  checked: 0,
  notInPimo: 0,
});

const UNASSIGNED_KEY = "__unassigned__";

const summarizeByOe = (
  changes: DcRelevanceChange[],
  unchangedItems: DcRelevanceChange[],
  notFound: DcRelevanceImportResult["excelIdsNotFound"],
  unassignedLabel: string
): OeSummary[] => {
  const byOe = new Map<string, OeSummary>();
  const summaryFor = (projectId: string, projectName: string) => {
    const summary = byOe.get(projectId) ?? emptySummary(projectName);
    byOe.set(projectId, summary);
    return summary;
  };
  for (const change of changes) {
    const summary = summaryFor(change.projectId, change.projectName);
    if (change.from === "No") summary.noToYes++;
    else if (change.from === "Yes") summary.yesToNo++;
    else if (change.to === "Yes") summary.notSetToYes++;
    else summary.notSetToNo++;
    summary.total++;
    summary.checked++;
  }
  for (const item of unchangedItems) {
    const summary = summaryFor(item.projectId, item.projectName);
    summary.unchanged++;
    summary.checked++;
  }
  for (const row of notFound) {
    const summary = row.projectId
      ? summaryFor(row.projectId, row.projectName ?? row.projectId)
      : summaryFor(UNASSIGNED_KEY, unassignedLabel);
    summary.notInPimo++;
  }
  const unassigned = byOe.get(UNASSIGNED_KEY);
  byOe.delete(UNASSIGNED_KEY);
  const sorted = [...byOe.values()].sort((x, y) =>
    x.projectName.localeCompare(y.projectName)
  );
  return unassigned ? [...sorted, unassigned] : sorted;
};

const sumSummaries = (summaries: OeSummary[], projectName: string) =>
  summaries.reduce((all, summary) => {
    (Object.keys(all) as (keyof OeSummary)[]).forEach((key) => {
      if (key !== "projectName") all[key] += summary[key];
    });
    return all;
  }, emptySummary(projectName));

const summaryColumns = [
  "notSetToYes",
  "notSetToNo",
  "noToYes",
  "yesToNo",
  "total",
  "unchanged",
  "checked",
  "notInPimo",
] as const;

// A real workbook instead of CSV: Excel picks the CSV separator from the
// machine's locale, so no single separator opens in columns everywhere.
const downloadXlsx = async (
  fileName: string,
  sheetName: string,
  rows: string[][]
) => {
  // Loaded on click so exceljs stays out of the page bundle.
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  sheet.addRows(rows);
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((column, index) => {
    const longest = Math.max(...rows.map((row) => row[index]?.length ?? 0));
    column.width = Math.min(MAX_COLUMN_WIDTH, longest + 2);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const DcRelevancePreviewModal = ({
  open,
  onClose,
  preview,
  language,
}: DcRelevancePreviewModalProps) => {
  const [tab, setTab] = useState<PreviewTab>("changes");
  const [downloadFailed, setDownloadFailed] = useState(false);

  const t = (key: string, params?: Record<string, string | number>) =>
    getLocalizedMessage(
      language,
      `manageProjects.dcRelevanceImport.modal.${key}`,
      params
    );

  const toYes = preview.changes.filter((c) => c.to === "Yes").length;
  const toNo = preview.changes.length - toYes;
  const unassignedLabel = t("unassignedRow");
  const oeSummaries = useMemo(
    () =>
      summarizeByOe(
        preview.changes,
        preview.unchangedItems,
        preview.excelIdsNotFound,
        unassignedLabel
      ),
    [
      preview.changes,
      preview.unchangedItems,
      preview.excelIdsNotFound,
      unassignedLabel,
    ]
  );
  const notFoundIdCount = new Set(
    preview.excelIdsNotFound.map((row) => row.id.trim().toLowerCase())
  ).size;
  const oeTotals = sumSummaries(oeSummaries, t("totalRow"));

  const changeRows = (changes: DcRelevanceChange[]) => [
    [t("oe"), t("app"), t("archerId"), t("adoitId"), t("from"), t("to")],
    ...changes.map((change) => [
      change.projectName,
      change.appName,
      change.customId,
      change.adoitIds.join(", "),
      change.from ?? "",
      change.to,
    ]),
  ];

  const handleDownload = async () => {
    const exports: Record<
      PreviewTab,
      { file: string; sheet: string; rows: string[][] }
    > = {
      changes: {
        file: "dc-relevance-changes",
        sheet: t("changesSheet"),
        rows: changeRows(preview.changes),
      },
      byOe: {
        file: "dc-relevance-by-oe",
        sheet: t("byOeSheet"),
        rows: [
          [t("oe"), ...summaryColumns.map((column) => t(column))],
          ...[...oeSummaries, oeTotals].map((summary) => [
            summary.projectName,
            ...summaryColumns.map((column) => String(summary[column])),
          ]),
        ],
      },
      unchanged: {
        file: "dc-relevance-unchanged",
        sheet: t("unchangedSheet"),
        rows: changeRows(preview.unchangedItems),
      },
      notFound: {
        file: "dc-relevance-not-in-pimo",
        sheet: t("notFoundSheet"),
        rows: [
          [t("adoitId"), t("app"), t("oe")],
          ...preview.excelIdsNotFound.map((row) => [
            row.id,
            row.name,
            row.projectName ?? "",
          ]),
        ],
      },
    };
    const { file, sheet, rows } = exports[tab];
    setDownloadFailed(false);
    try {
      await downloadXlsx(
        `${file}-${dayjs().format("YYYY-MM-DD")}.xlsx`,
        sheet,
        rows
      );
    } catch (error) {
      console.error("DC Relevance preview download failed:", error);
      setDownloadFailed(true);
    }
  };

  return (
    <PMPopUp
      open={open}
      title={t("title")}
      onClose={onClose}
      maxWidth="lg"
      cancelButtonText={t("close")}
      cancelButtonFireEvent={onClose}
      customButtonText={t("downloadExcel")}
      customButtonVariant="outlined"
      customButtonFireEvent={handleDownload}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography sx={{ fontSize: "14px", color: grey[700] }}>
          {t("summary", {
            inScope: preview.itemsInScope,
            changes: preview.changes.length,
            yes: toYes,
            no: toNo,
            unchanged: preview.unchanged,
            excelIds: preview.excelIdCount,
            found: Math.max(preview.excelIdCount - notFoundIdCount, 0),
            notFound: notFoundIdCount,
          })}
        </Typography>

        {downloadFailed && (
          <Typography
            sx={{ fontSize: "14px", color: "error.main", fontWeight: 500 }}
          >
            {t("downloadFailed")}
          </Typography>
        )}

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab
            value="changes"
            label={t("changesTab", { count: preview.changes.length })}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value="byOe"
            label={t("byOeTab", { count: oeSummaries.length })}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value="unchanged"
            label={t("unchangedTab", { count: preview.unchangedItems.length })}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value="notFound"
            label={t("notFoundTab", {
              count: preview.excelIdsNotFound.length,
            })}
            sx={{ textTransform: "none" }}
          />
        </Tabs>

        {tab === "changes" || tab === "unchanged" ? (
          <TableContainer sx={{ maxHeight: "55vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{t("oe")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("app")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("archerId")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("adoitId")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("from")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("to")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tab === "changes"
                  ? preview.changes
                  : preview.unchangedItems
                ).map((change) => (
                  <TableRow key={change.reportingItemId}>
                    <TableCell>{change.projectName}</TableCell>
                    <TableCell>{change.appName}</TableCell>
                    <TableCell>{change.customId}</TableCell>
                    <TableCell sx={{ wordBreak: "break-all" }}>
                      {change.adoitIds.join(", ") || t("noAdoitId")}
                    </TableCell>
                    <TableCell sx={valueCellSx(change.from)}>
                      {change.from ?? t("notSet")}
                    </TableCell>
                    <TableCell sx={valueCellSx(change.to)}>
                      {change.to}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tab === "byOe" ? (
          <TableContainer sx={{ maxHeight: "55vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{t("oe")}</TableCell>
                  {summaryColumns.map((column) => (
                    <TableCell key={column} sx={headerCellSx} align="right">
                      {t(column)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {oeSummaries.map((summary) => (
                  <TableRow key={summary.projectName}>
                    <TableCell>{summary.projectName}</TableCell>
                    {summaryColumns.map((column) => (
                      <TableCell
                        key={column}
                        align="right"
                        sx={
                          column === "total" ? { fontWeight: 500 } : undefined
                        }
                      >
                        {summary[column]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {oeTotals.projectName}
                  </TableCell>
                  {summaryColumns.map((column) => (
                    <TableCell
                      key={column}
                      align="right"
                      sx={{ fontWeight: 500 }}
                    >
                      {oeTotals[column]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer sx={{ maxHeight: "55vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{t("adoitId")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("app")}</TableCell>
                  <TableCell sx={headerCellSx}>{t("oe")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.excelIdsNotFound.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ wordBreak: "break-all" }}>
                      {row.id}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.projectName ?? "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </PMPopUp>
  );
};
